import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Button,
  Tooltip
} from '@mui/material';
import {
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Visibility as VisibilityIcon,
  Description as DocumentIcon,
  Assignment as CertificateIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  selectFlaggedCertificates,
  selectFlaggedDocuments,
  selectFlaggedSupplementalDocuments,
  unflagCertificate,
  unflagDocument,
  unflagSupplementalDocument
} from 'redux/flaggedItemsSlice';
import StatusIndicator from 'components/shared/StatusIndicator';
import { format } from 'date-fns';
import { organizationState } from 'redux/organizationSlice';
import { getNavigationPath, useNavPath, NAVIGATED_FROM } from 'constants/routes';

export default function FlaggedItems() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedClient, userOrgType } = useSelector(organizationState);
  const NAV_PATH = useNavPath(userOrgType);
  const flaggedCertificates = useSelector(selectFlaggedCertificates);
  const flaggedDocuments = useSelector(selectFlaggedDocuments);
  const flaggedSupplementalDocuments = useSelector(selectFlaggedSupplementalDocuments);
  
  // Combine and sort all flagged items by flagged date
  const allFlaggedItems = [
    ...flaggedCertificates,
    ...flaggedDocuments,
    ...flaggedSupplementalDocuments
  ].sort((a, b) => new Date(b.flaggedAt) - new Date(a.flaggedAt));
  
  const handleUnflag = (item) => {
    if (item.type === 'certificate') {
      dispatch(unflagCertificate(item.id));
    } else if (item.type === 'document') {
      dispatch(unflagDocument(item.id));
    } else if (item.type === 'supplemental') {
      dispatch(unflagSupplementalDocument(item.id));
    }
  };
  
  const handleView = (item) => {
    if (item.type === 'certificate') {
      // Navigate to certificate details using proper routing structure
      const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
        clientId: selectedClient?.id || item.clientID,
        vendorId: item.clientVendorID || NAVIGATED_FROM.CERTIFICATES,
        certificateId: item.id
      });
      navigate(certificatePath);
    } else if (item.type === 'document') {
      // Navigate to document view
      if (item.certificateId) {
        const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
          clientId: selectedClient?.id || item.clientID,
          vendorId: item.clientVendorID || NAVIGATED_FROM.CERTIFICATES,
          certificateId: item.certificateId
        });
        navigate(certificatePath);
      } else {
        navigate(`/document/${item.id}`);
      }
    } else if (item.type === 'supplemental') {
      // Navigate to parent certificate or document view
      if (item.parentCertificateId) {
        const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
          clientId: selectedClient?.id || item.clientID,
          vendorId: item.clientVendorID || NAVIGATED_FROM.CERTIFICATES,
          certificateId: item.parentCertificateId
        });
        navigate(certificatePath);
      } else if (item.certificateId) {
        const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
          clientId: selectedClient?.id || item.clientID,
          vendorId: item.clientVendorID || NAVIGATED_FROM.CERTIFICATES,
          certificateId: item.certificateId
        });
        navigate(certificatePath);
      } else {
        navigate(`/document/${item.id}`);
      }
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch {
      return '';
    }
  };
  
  if (allFlaggedItems.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Flagged Items
        </Typography>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No flagged items
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Flag certificates or documents to quickly access them here
          </Typography>
        </Box>
      </Paper>
    );
  }
  
  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Flagged Items
        </Typography>
        <Chip 
          label={`${allFlaggedItems.length} item${allFlaggedItems.length !== 1 ? 's' : ''}`}
          size="small"
          color="primary"
        />
      </Box>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Vendor/Details</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Flagged</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allFlaggedItems.map((item) => (
              <TableRow key={`${item.type}-${item.id}`} hover>
                <TableCell>
                  <Chip
                    icon={item.type === 'certificate' ? <CertificateIcon /> : <DocumentIcon />}
                    label={
                      item.type === 'certificate' ? 'Certificate' : 
                      item.type === 'supplemental' ? 'Supplemental' : 
                      'Document'
                    }
                    size="small"
                    variant="outlined"
                    color={
                      item.type === 'certificate' ? 'primary' : 
                      item.type === 'supplemental' ? 'info' :
                      'secondary'
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { 
                        textDecoration: 'underline',
                        color: 'primary.main'
                      }
                    }}
                    onClick={() => handleView(item)}
                  >
                    {item.name || 'Unnamed'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {item.vendorName || item.insured || '-'}
                  </Typography>
                  {item.certificateDate && (
                    <Typography variant="caption" color="text.secondary">
                      Cert Date: {formatDate(item.certificateDate)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {item.status ? (
                    <StatusIndicator label={item.status} size="small" />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatDate(item.flaggedAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(item.flaggedAt)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="View">
                      <IconButton 
                        size="small" 
                        onClick={() => handleView(item)}
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove flag">
                      <IconButton 
                        size="small" 
                        onClick={() => handleUnflag(item)}
                        color="error"
                      >
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {allFlaggedItems.length > 5 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button 
            size="small" 
            color="primary"
            onClick={() => navigate('/flagged-items')}
          >
            View All Flagged Items
          </Button>
        </Box>
      )}
    </Paper>
  );
}